import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, getToken, setToken } from '@/api/client';
import { clearResourceCache } from '@/api/useCachedResource';

export interface User {
  id: string;
  email: string;
  name?: string;
  role?: 'user' | 'admin';
}

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

/** Signup intent carried from the marketing pricing CTA — the plan + offer the
 *  visitor chose. Forwarded to register/google so the app can prompt the purchase
 *  after signup (pay-now/pay-later). Re-validated server-side. */
export interface SignupIntent {
  planName: string;
  cycle?: string | undefined;
  couponCode?: string | undefined;
  ref?: string | undefined;
}

interface AuthContextValue extends AuthState {
  login(email: string, password: string): Promise<void>;
  /** Sign in — and sign up on first use — with a Google ID token from Google
   *  Identity Services. Resolves `{ waitlisted: true }` when the platform is at
   *  its seat cap (account created, no session issued) — same contract as
   *  register(). Throws on a real failure (bad token, disabled, existing
   *  waitlisted account) for the caller to surface. */
  loginWithGoogle(idToken: string, intent?: SignupIntent): Promise<{ waitlisted: boolean }>;
  /** Resolves `{ waitlisted: true }` when the platform is at its seat cap — the
   *  account exists but no session was issued, so the caller shows the waitlist
   *  screen instead of navigating into the app. */
  register(
    email: string,
    password: string,
    name: string,
    salesProfile?: Record<string, unknown>,
    intent?: SignupIntent,
  ): Promise<{ waitlisted: boolean }>;
  logout(): Promise<void>;
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Cache the last known user next to the token so a reload can restore the
// logged-in UI INSTANTLY (no blank wait on /api/auth/me) and — critically — a
// TRANSIENT network error on revalidation never wipes a still-valid session.
const USER_KEY = 'brew.user';
function getCachedUser(): User | null {
  try { const s = localStorage.getItem(USER_KEY); return s ? (JSON.parse(s) as User) : null; } catch { return null; }
}
function setCachedUser(user: User | null): void {
  try { if (user) localStorage.setItem(USER_KEY, JSON.stringify(user)); else localStorage.removeItem(USER_KEY); } catch { /* private mode */ }
}
/** A 401/403 means the token is genuinely dead → log out. Anything else (a
 *  network "Failed to fetch", a server restart, a 5xx, a dev-proxy race) is
 *  TRANSIENT and must NOT destroy a valid session. */
function isAuthRejection(err: unknown): boolean {
  const s = (err as { status?: number } | null)?.status;
  return s === 401 || s === 403;
}

/** Provider. Wrap the app once at the root. Restores the session on mount from
 *  the cached user (optimistic), then revalidates against /api/auth/me. */
export function AuthProvider({ children }: { children: ReactNode }) {
  // Optimistic bootstrap: if we hold a token + a cached user, render logged-in
  // immediately (loading:false) so a refresh is instant. Revalidation happens in
  // the background and only downgrades on a real auth rejection.
  const bootUser = getToken() ? getCachedUser() : null;
  const [state, setState] = useState<AuthState>({ user: bootUser, loading: !bootUser && !!getToken(), error: null });

  async function refresh(): Promise<void> {
    if (!getToken()) {
      setCachedUser(null);
      setState({ user: null, loading: false, error: null });
      return;
    }
    // Retry a TRANSIENT failure a few times before giving up — a blip must not
    // log the user out. Only a genuine 401/403 clears the session.
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const me = await api.get<{ user: User }>('/api/auth/me');
        setCachedUser(me.user);
        setState({ user: me.user, loading: false, error: null });
        return;
      } catch (err) {
        if (isAuthRejection(err)) {
          setToken(null);
          setCachedUser(null);
          setState({ user: null, loading: false, error: (err as Error).message });
          return;
        }
        // Transient — back off and retry.
        await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }
    }
    // Couldn't confirm after retries, but the token/cached user may still be
    // valid. KEEP them (stay logged in on the cached user); a later navigation or
    // the browser 'online' event will revalidate. Never wipe the token here.
    setState((s) => ({ ...s, loading: false, error: 'auth revalidation deferred (offline?)' }));
  }

  useEffect(() => {
    void refresh();
    // Revalidate when the tab comes back online / regains focus — recovers a
    // session that couldn't be confirmed during a transient outage.
    const onOnline = () => { if (getToken()) void refresh(); };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  async function login(email: string, password: string): Promise<void> {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const result = await api.post<{ accessToken: string; user: User }>(
        '/api/auth/login',
        { email, password },
      );
      setToken(result.accessToken);
      setCachedUser(result.user);
      setState({ user: result.user, loading: false, error: null });
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: (err as Error).message }));
      throw err;
    }
  }

  async function loginWithGoogle(idToken: string, intent?: SignupIntent): Promise<{ waitlisted: boolean }> {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const result = await api.post<{ accessToken?: string; user: User; waitlisted?: boolean }>(
        '/api/auth/google',
        { idToken, ...(intent ? { intent } : {}) },
      );
      // Seat cap hit on first sign-in: account created, no session. Stay logged
      // out — the caller renders the waitlist message.
      if (result.waitlisted || !result.accessToken) {
        setState({ user: null, loading: false, error: null });
        return { waitlisted: true };
      }
      // Identical session-establishment path as login(): token + cached user + state.
      setToken(result.accessToken);
      setCachedUser(result.user);
      setState({ user: result.user, loading: false, error: null });
      return { waitlisted: false };
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: (err as Error).message }));
      throw err;
    }
  }

  async function register(
    email: string,
    password: string,
    name: string,
    salesProfile?: Record<string, unknown>,
    intent?: SignupIntent,
  ): Promise<{ waitlisted: boolean }> {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const result = await api.post<{ accessToken?: string; user: User; waitlisted?: boolean }>(
        '/api/auth/register',
        { email, password, name, salesProfile, ...(intent ? { intent } : {}) },
      );
      // Seat cap hit: the account was created but no session issued. Stay
      // logged out — the caller renders the waitlist screen.
      if (result.waitlisted || !result.accessToken) {
        setState({ user: null, loading: false, error: null });
        return { waitlisted: true };
      }
      setToken(result.accessToken);
      setCachedUser(result.user);
      setState({ user: result.user, loading: false, error: null });
      return { waitlisted: false };
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: (err as Error).message }));
      throw err;
    }
  }

  async function logout(): Promise<void> {
    try {
      await api.post('/api/auth/logout', {});
    } catch {
      /* best-effort; clear local state regardless */
    }
    setToken(null);
    setCachedUser(null);
    clearResourceCache(); // don't let the next user read this user's cached data
    setState({ user: null, loading: false, error: null });
  }

  const value: AuthContextValue = { ...state, login, loginWithGoogle, register, logout, refresh };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
